import { Capacitor } from '@capacitor/core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { format, parseISO } from 'date-fns/esm'
import { nl } from 'date-fns/locale'
import { AnimatePresence, motion } from 'framer-motion'
import Lottie from 'lottie-react'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Skeleton } from '@/@bothrs-ui/web/components/skeleton/Skeleton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import LoadingAnimation from '@/assets/lottie/loading-animation.json'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { capitalize } from '@/modules/shared/helpers/locale'

import type { SlotWithLocationAndDoctor } from '../types/booking.type'

type DateSelectionCarouselProps = {
  groupedSlots: Record<string, SlotWithLocationAndDoctor[]>
  selectedDate: string
  setSelectedDate: (date: string) => void
  hasMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  onLoadMoreDates: () => void
}

function Indicators({ onBack, onNext }: { onBack?: () => void; onNext?: () => void }) {
  const isNative = Capacitor.isNativePlatform()

  if (isNative) return null

  return (
    <StIndicatorsContainer>
      {onBack ? (
        <StIndicatorOverlayLeft>
          <StIndicatorButton icon={['fal', 'chevron-left']} onClick={onBack} variant='Primary' size='small' />
        </StIndicatorOverlayLeft>
      ) : (
        <div />
      )}
      {onNext ? (
        <StIndicatorOverlayRight>
          <StIndicatorButton icon={['fal', 'chevron-right']} onClick={onNext} variant='Primary' size='small' />
        </StIndicatorOverlayRight>
      ) : (
        <div />
      )}
    </StIndicatorsContainer>
  )
}

export const DateSelectionCarousel = ({
  groupedSlots,
  selectedDate,
  setSelectedDate,
  hasMore,
  isLoading,
  isLoadingMore,
  onLoadMoreDates,
}: DateSelectionCarouselProps) => {
  const [onPreviousScrollVisible, setOnPreviousScrollVisible] = useState<boolean>(false)
  const theme = useTheme()
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrollRefWrapperRef = useRef<HTMLDivElement>(null)
  const itemReferences = useRef<Record<string, HTMLDivElement | null>>({})
  const lastLoadMoreTriggerAtRef = useRef<number>(0)
  const dates = useMemo(() => Object.keys(groupedSlots), [groupedSlots])

  // The refill below must follow the data, not the callback. A page response
  // lands in the same render that clears isLoadingMore, which gives
  // onLoadMoreDates a new identity while the accumulator has not yet folded the
  // new slots in: re-running on that identity would ask for the same page again
  // from the old frontier. Reading the callback through a ref lets the effect
  // wait for `dates` to change instead, i.e. for the batch to be incorporated.
  const onLoadMoreDatesRef = useRef(onLoadMoreDates)
  onLoadMoreDatesRef.current = onLoadMoreDates

  useEffect(() => {
    const COOLDOWN_MS = 600

    const triggerLoadMore = (bypassCooldown: boolean = false) => {
      if (!scrollRef.current || !hasMore) return
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      const preloadPx = 200
      const nearEnd = scrollLeft + clientWidth >= scrollWidth - preloadPx
      const listTooShort = scrollWidth <= clientWidth

      if (nearEnd || listTooShort) {
        const now = Date.now()
        if (!bypassCooldown && now - lastLoadMoreTriggerAtRef.current < COOLDOWN_MS) return
        lastLoadMoreTriggerAtRef.current = now
        onLoadMoreDatesRef.current()
      }
    }

    const handleScroll = () => {
      // Throttle load-more triggers while smooth scrolling
      triggerLoadMore()
    }

    // Initial check when the dates change: bypass cooldown to quickly fill
    triggerLoadMore(true)

    const element = scrollRef.current
    element?.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      element?.removeEventListener('scroll', handleScroll as EventListener)
    }
  }, [hasMore, dates])

  useEffect(() => {
    const currentScrollRef = scrollRef.current
    function onScroll() {
      if (currentScrollRef?.scrollLeft === 0) {
        setOnPreviousScrollVisible(false)
      } else {
        setOnPreviousScrollVisible(true)
      }
    }

    currentScrollRef?.addEventListener('scroll', onScroll)
    return () => currentScrollRef?.removeEventListener('scroll', onScroll)
  }, [scrollRef.current?.scrollLeft])

  // Scroll the selected card into view and center it in the viewport
  useLayoutEffect(() => {
    if (!selectedDate) return
    const container = scrollRef.current
    const element = itemReferences.current[selectedDate]
    if (!container || !element) return

    const containerWidth = container.clientWidth
    const scrollWidth = container.scrollWidth
    const elementLeft = element.offsetLeft
    const computedStyles = getComputedStyle(container)
    const paddingLeft = Number.parseInt(computedStyles.paddingLeft || '0', 10) || 0
    const elementWidth = element.offsetWidth
    // Center selected item: position its midpoint at the container's midpoint
    const desiredLeft = elementLeft - (containerWidth - elementWidth) / 2 - paddingLeft
    // Clamp within scroll range
    const maxScrollLeft = Math.max(0, scrollWidth - containerWidth)
    const targetLeft = Math.min(Math.max(0, desiredLeft), maxScrollLeft)

    container.scrollTo({ left: targetLeft, behavior: 'smooth' })
  }, [selectedDate])

  const onScrollToRight = useCallback(() => {
    const currentScrollLeft = scrollRef.current?.scrollLeft ?? 0
    const scrollAmount = scrollRefWrapperRef.current?.clientWidth ?? 200 // Use wrapper width as scroll amount
    const targetScrollLeft = Math.max(0, currentScrollLeft + scrollAmount) // Ensure scroll position is never below 0

    scrollRef.current?.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    })
  }, [])

  const onScrollToLeft = useCallback(() => {
    const currentScrollLeft = scrollRef.current?.scrollLeft ?? 0
    const scrollAmount = scrollRefWrapperRef.current?.clientWidth ?? 200 // Use wrapper width as scroll amount
    const targetScrollLeft = Math.max(0, currentScrollLeft - scrollAmount) // Ensure scroll position is never below 0

    setOnPreviousScrollVisible(targetScrollLeft === 0)

    scrollRef.current?.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    })
  }, [])

  return (
    <StScrollRowWrapper ref={scrollRefWrapperRef}>
      <Indicators onNext={onScrollToRight} onBack={onPreviousScrollVisible ? onScrollToLeft : undefined} />
      <StScrollRow ref={scrollRef} $isLoading={isLoading && dates.length > 0}>
        {isLoading && dates.length === 0
          ? Array.from({ length: 5 }).map((_, index) => (
              <StSkeletonCard key={index}>
                <FlexColumn gap={theme.UI.SpacingPx.Space2}>
                  <Skeleton style={{ width: '70px' }} />
                  <Skeleton style={{ width: '120px' }} />
                </FlexColumn>
              </StSkeletonCard>
            ))
          : null}
        {dates.map((date, index) => {
          const isSelected = selectedDate === date
          const isNearEnd = index >= Math.max(0, dates.length - 2)
          const parsedDate = parseISO(date)
          return (
            <div
              key={date}
              ref={(element) => {
                itemReferences.current[date] = element as HTMLDivElement | null
              }}
              style={{ display: 'flex' }}
            >
              <Card
                style={{
                  width: 'auto',
                  border: `1px solid ${isSelected ? theme.Components.Card.ActiveBorder : theme.Components.Card.Border}`,
                  transition: 'border 175ms ease-out',
                }}
                onClick={() => {
                  setSelectedDate(date)
                  if (hasMore && isNearEnd) {
                    onLoadMoreDates()
                  }
                }}
              >
                <FlexRow justifyContent='space-between' gap={theme.UI.SpacingPx.Space4}>
                  <FlexColumn>
                    <Span
                      type='BodyMediumRegular'
                      style={{
                        fontWeight: isSelected ? 500 : 400,
                      }}
                    >
                      {capitalize(format(parsedDate, 'EEEE', { locale: nl }))}
                    </Span>
                    <Span
                      type='BodyMediumRegular'
                      style={{
                        width: 'max-content',
                        fontWeight: isSelected ? 500 : 400,
                      }}
                    >
                      {format(parsedDate, 'd MMMM yyyy', { locale: nl })}
                    </Span>
                  </FlexColumn>
                  <StCheckboxContainer>
                    <AnimatePresence>
                      {isSelected ? (
                        <AnimatedStIcon
                          key={date}
                          {...getDropInProps({
                            hidden: { y: 10 },
                            exit: { opacity: 0, y: 10 },
                          })}
                        >
                          <FontAwesomeIcon icon={['fal', 'check']} color={theme.Colors.Neutrals.Lightest} />
                        </AnimatedStIcon>
                      ) : null}
                    </AnimatePresence>
                    <StCheckbox checked={isSelected} id={date} type='checkbox' readOnly />
                  </StCheckboxContainer>
                </FlexRow>
              </Card>
            </div>
          )
        })}

        {/* Always render a tail placeholder with stable width to prevent layout shifts.
            The animation tracks a request that is actually running — tying it to
            hasMore alone left it spinning for as long as more dates existed. At
            rest a chevron still signals that more dates can be loaded, which on
            native is the only such cue: Indicators renders nothing there. */}
        <StTailPlaceholder role='status'>
          {isLoadingMore ? (
            <>
              <Lottie animationData={LoadingAnimation} loop style={{ width: 40, height: 40 }} />
              <StVisuallyHidden>
                {t({
                  id: 'appointments.book.slots.loading-more-dates',
                  message: 'Meer datums laden',
                })}
              </StVisuallyHidden>
            </>
          ) : hasMore ? (
            <FontAwesomeIcon icon={['fal', 'chevron-right']} color={theme.Colors.Neutrals.Dark} />
          ) : null}
        </StTailPlaceholder>
      </StScrollRow>
    </StScrollRowWrapper>
  )
}

const StIndicatorsContainer = styled(FlexRow)`
  gap: ${({ theme }) => theme.UI.SpacingPx.Space2};
  justify-content: space-between;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  top: 0;
  z-index: 1;
  pointer-events: none;
`

const StIndicatorButton = styled(IconButton)`
  border-radius: 99px;
  border: 0.667px solid
    var(--Components-Button-Primary-Border, rgba(0, 0, 0, 0));
  background: var(--color-primary-30, #03387830);
  display: flex;
  width: 26px;
  height: 26px;
  padding: 8px;
  justify-content: center;
  align-items: center;
  gap: 5.333px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  pointer-events: auto;
`

const StIndicatorOverlayLeft = styled.div`
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 126px;
  background: linear-gradient(90deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
`

const StIndicatorOverlayRight = styled.div`
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 126px;
  background: linear-gradient(270deg, #fff 0%, rgba(255, 255, 255, 0) 100%);
`

const StCheckboxContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 16px;
  height: 16px;
  min-width: 16px;
  min-height: 16px;
  max-width: 16px;
  max-height: 16px;
  margin-left: 1px;
  margin-top: 3px;
  flex-grow: 0;
`

const StCheckbox = styled.input<{ checked: boolean }>`
  border: 1px solid
    ${({ theme, checked }) => (checked ? theme.Components.Card.ActiveBorder : theme.Components.Card.Border)};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  width: 100%;
  background-color: ${({ theme, checked }) => (checked ? theme.Components.Card.ActiveBorder : 'transparent')};
  transition: background-color 175ms ease-out;
`

const StIcon = styled(motion.div)`
  position: absolute;
  z-index: 1;
  width: 12px;
  height: 12px;
  top: 2px;
  left: 2px;
  bottom: 2px;
  right: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const AnimatedStIcon = motion(StIcon)

const StScrollRow = styled.div<{ $isLoading?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  transform: translateX(0);
  gap: ${({ theme }) => theme?.UI?.SpacingPx.Space2};
  overflow: auto;
  margin: 0 -${({ theme }) => theme?.UI?.SpacingPx.Space4};
  padding: 0 ${({ theme }) => theme?.UI?.SpacingPx.Space4};
  /* Prevent UA scroll anchoring jumps when items mount/unmount */
  overflow-anchor: none;
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  transition: opacity 150ms ease-out;
  pointer-events: ${({ $isLoading }) => ($isLoading ? 'none' : 'auto')};
`

const StScrollRowWrapper = styled.div`
  transform: translateX(0);
  overflow: hidden;
  padding: 0;
`

const StSkeletonCard = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: ${({ theme }) => theme.UI.SpacingPx.Space4};
  border: 1px solid ${({ theme }) => theme.Components.Card.Border};
  border-radius: ${({ theme }) => theme.UI.BorderRadius.Small}px;
  background: ${({ theme }) => theme.Components.Card.Fill};
`

const StTailPlaceholder = styled.div`
  position: relative;
  min-width: 170px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border 175ms ease-out;
`

const StVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
